import React, { useState, useEffect } from 'react'
import { EmojiDictionaryEntry } from '../types'
import { EmojiDictionary } from '../services/EmojiDictionary'
import { gospelTerms } from '../utils/gospelTerms'

interface EmojiDictionaryModalProps {
	isOpen: boolean;
	onClose: () => void;
}

// Component to render emoji or gospel symbol
const EmojiDisplay: React.FC<{ emoji: string; isGospelSymbol: boolean }> = ({ emoji, isGospelSymbol }) => {
	if (isGospelSymbol) {
		const term = emoji.substring(1) // Remove the '!' prefix
		const gospelTerm = gospelTerms[term]
		if (gospelTerm) {
			return (
				<div className="gospel-symbol-display">
					<img
						src={gospelTerm.src}
						alt={gospelTerm.label}
						className="gospel-symbol-image"
					/>
					<span className="gospel-symbol-text">{emoji}</span>
				</div>
			)
		}
		// Fallback for unknown gospel symbols
		return <span className="unknown-gospel-symbol">{emoji}</span>
	}
	return <span className="emoji-symbol">{emoji}</span>
}

export const EmojiDictionaryModal: React.FC<EmojiDictionaryModalProps> = ({
	isOpen,
	onClose,
}) => {
	const [entries, setEntries] = useState<EmojiDictionaryEntry[]>([])
	const [newWord, setNewWord] = useState('')
	const [newEmoji, setNewEmoji] = useState('')
	const [searchTerm, setSearchTerm] = useState('')
	const [showTextEditor, setShowTextEditor] = useState(false)
	const [dictionaryText, setDictionaryText] = useState('')

	useEffect(() => {
		if (isOpen) {
			setEntries(EmojiDictionary.getEntries())
			setDictionaryText(EmojiDictionary.getText())
		}
	}, [isOpen])

	const handleAddEntry = () => {
		if (newWord.trim() && newEmoji.trim()) {
			EmojiDictionary.addEntry(newWord.trim(), newEmoji.trim())
			setEntries(EmojiDictionary.getEntries())
			setNewWord('')
			setNewEmoji('')
		}
	}

	const handleRemoveEntry = (wordsStr: string) => {
		const words = wordsStr.split(', ')
		for (const word of words) {
			EmojiDictionary.removeEntry(word)
		}
		setEntries(EmojiDictionary.getEntries())
	}

	const handleResetToDefaults = () => {
		if (confirm('Reset to default emoji dictionary? This will remove all custom entries.')) {
			EmojiDictionary.resetToDefaults()
			setEntries(EmojiDictionary.getEntries())
			setDictionaryText(EmojiDictionary.getText())
		}
	}

	const handleSaveText = () => {
		const result = EmojiDictionary.importFromText(dictionaryText)
		if (result.success) {
			setEntries(EmojiDictionary.getEntries())
			setShowTextEditor(false)
		} else {
			alert(`Error saving dictionary: ${result.error}`)
		}
	}

	const handleExport = () => {
		const text = EmojiDictionary.exportAsText()
		const blob = new Blob([text], { type: 'text/plain' })
		const url = URL.createObjectURL(blob)
		const a = document.createElement('a')
		a.href = url
		a.download = 'memtastic-emoji-dictionary.txt'
		a.click()
		URL.revokeObjectURL(url)
	}

	const handleImport = (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0]
		if (file) {
			const reader = new FileReader()
			reader.onload = (e) => {
				const text = e.target?.result as string
				const result = EmojiDictionary.importFromText(text)
				if (result.success) {
					setEntries(EmojiDictionary.getEntries())
					setDictionaryText(EmojiDictionary.getText())
					alert('Dictionary imported successfully!')
				} else {
					alert(`Error importing dictionary: ${result.error}`)
				}
			}
			reader.readAsText(file)
		}
		// Reset input
		event.target.value = ''
	}

	// Group entries by emoji for display
	const groupedEntries = entries.reduce((groups, entry) => {
		if (!groups[entry.emoji]) {
			groups[entry.emoji] = []
		}
		groups[entry.emoji].push(entry.word)
		return groups
	}, {} as Record<string, string[]>)

	// Convert to array format for filtering and display
	const displayEntries = Object.entries(groupedEntries).map(([emoji, words]) => ({
		words: words.sort((a, b) => a.localeCompare(b)),
		emoji,
		wordsStr: words.sort((a, b) => a.localeCompare(b)).join(', '),
		isGospelSymbol: emoji.startsWith('!'),
	}))

	const filteredEntries = displayEntries.filter(entry =>
		entry.wordsStr.toLowerCase().includes(searchTerm.toLowerCase()) ||
		entry.emoji.includes(searchTerm),
	)

	if (!isOpen) return null

	return (
		<div className="modal-overlay" onClick={onClose}>
			<div className="modal-content emoji-dictionary-modal" onClick={e => e.stopPropagation()}>
				<div className="modal-header">
					<h2>Emoji Dictionary</h2>
					<button className="modal-close" onClick={onClose}>×</button>
				</div>

				<div className="modal-body">
					<div className="emoji-dictionary-tabs">
						<button
							className={`tab-button ${!showTextEditor ? 'active' : ''}`}
							onClick={() => setShowTextEditor(false)}
						>
							Visual Editor
						</button>
						<button
							className={`tab-button ${showTextEditor ? 'active' : ''}`}
							onClick={() => setShowTextEditor(true)}
						>
							Text Editor
						</button>
					</div>

					{showTextEditor ? (
						<div className="text-editor-section">
							<div className="text-editor-controls">
								<button onClick={handleSaveText} className="btn btn-primary">
									Save Changes
								</button>
								<button onClick={() => setDictionaryText(EmojiDictionary.getText())} className="btn btn-secondary">
									Reset
								</button>
							</div>
							<textarea
								value={dictionaryText}
								onChange={(e) => setDictionaryText(e.target.value)}
								className="dictionary-text-editor"
								placeholder="Enter emoji dictionary in format: word = emoji (or word1, word2 = emoji)"
								rows={20}
							/>
						</div>
					) : (
						<>
							<div className="emoji-dictionary-controls">
								<div className="add-entry-section">
									<h3>Add New Entry</h3>
									<div className="add-entry-form">
										<input
											type="text"
											placeholder="Word (or word1, word2, word3)"
											value={newWord}
											onChange={(e) => setNewWord(e.target.value)}
											onKeyDown={(e) => e.key === 'Enter' && handleAddEntry()}
										/>
										<input
											type="text"
											placeholder="Emoji/Gospel Symbol"
											value={newEmoji}
											onChange={(e) => setNewEmoji(e.target.value)}
											onKeyDown={(e) => e.key === 'Enter' && handleAddEntry()}
										/>
										<button onClick={handleAddEntry} className="btn btn-primary">
											Add
										</button>
									</div>
								</div>

								<div className="search-section">
									<input
										type="text"
										placeholder="Search entries..."
										value={searchTerm}
										onChange={(e) => setSearchTerm(e.target.value)}
										className="search-input"
									/>
								</div>
							</div>
						</>
					)}



					{!showTextEditor && (
						<div className="emoji-entries">
							<div className="entries-header">
								<span>Words</span>
								<span>Emoji</span>
								<span>Actions</span>
							</div>
							<div className="entries-list">
								{filteredEntries.map((entry, index) => (
									<div key={index} className="entry-row">
										<span className="entry-word">{entry.wordsStr}</span>
										<div className="entry-emoji">
											<EmojiDisplay emoji={entry.emoji} isGospelSymbol={entry.isGospelSymbol} />
										</div>
										<button
											onClick={() => handleRemoveEntry(entry.wordsStr)}
											className="btn btn-danger btn-small"
										>
											Remove
										</button>
									</div>
								))}
							</div>
						</div>
					)}

					<div className="dictionary-actions">
						<div className="action-group">
							<button onClick={handleResetToDefaults} className="btn btn-secondary">
								Reset to Defaults
							</button>
							<button onClick={handleExport} className="btn btn-secondary">
								Export
							</button>
							<label className="btn btn-secondary file-input-label">
								Import
								<input
									type="file"
									accept=".txt"
									onChange={handleImport}
									style={{ display: 'none' }}
								/>
							</label>
						</div>
						<p className="entry-count">
							{filteredEntries.length} of {displayEntries.length} entries
						</p>
					</div>
				</div>
			</div>
		</div>
	)
}
